import { useCallback, useEffect, useRef, useState } from "react";

interface UseExternallyDrivenExpansionResult<T> {
  expanded: boolean;
  autoFocus: boolean;
  expand: () => void;
  collapse: () => void;
  markLocalChange: (val: T) => void;
}

export function useExternallyDrivenExpansion<T>(
  propValue: T,
  shouldExpand: (val: T) => boolean,
): UseExternallyDrivenExpansionResult<T> {
  const [expanded, setExpanded] = useState(() => shouldExpand(propValue));
  // Focus is granted only when the user opens the card here via expand(). Expansion driven by
  // an external prop change (e.g. a template or copied shipment populating many fields at once)
  // reflects the expanded state but never steals focus onto an arbitrary input.
  const [autoFocus, setAutoFocus] = useState(false);
  const expectedRef = useRef(propValue);

  // Callers pass `shouldExpand` inline, so this effect re-runs on every render. The
  // `expectedRef` guard makes every run after a sync a no-op, so only a real external
  // change to `propValue` touches state.
  useEffect(() => {
    if (propValue !== expectedRef.current) {
      expectedRef.current = propValue;
      setExpanded(shouldExpand(propValue));
      setAutoFocus(false);
    }
  }, [propValue, shouldExpand]);

  const expand = useCallback(() => {
    setExpanded(true);
    setAutoFocus(true);
  }, []);

  const collapse = useCallback(() => {
    setExpanded(false);
    setAutoFocus(false);
  }, []);

  const markLocalChange = useCallback((val: T) => {
    expectedRef.current = val;
  }, []);

  return {
    expanded,
    autoFocus,
    expand,
    collapse,
    markLocalChange,
  };
}
